import { darken, lighten, rgba } from 'polished';
import { css } from '@emotion/react/macro';
import { PHI } from 'utils/variables';

const globalStyle = theme => css`
  :root {
    font-size: ${PHI * 8}px;
    --toastify-toast-min-height: 48px;
    --toastify-color-light: ${lighten(0.1, theme.colors.primaryColor)};
    --toastify-text-color-light: ${theme.colors.secondaryColor};
    --toastify-color-dark: ${theme.colors.blackColor};
    --toastify-color-info: ${theme.colors.blueColor};
    --toastify-color-success: ${theme.colors.greenColor};
    --toastify-color-warning: ${theme.colors.accentColor};
    --toastify-color-error: ${theme.colors.redColorDark};
  }

  * {
    outline: none;
    /* font-size: 1rem; */
    font-family: persian, roboto;
    box-sizing: border-box;
    letter-spacing: 0 !important;
    /* -webkit-tap-highlight-color: rgba(0,0,0,0); */
  }

  html,
  body {
    /* overflow-x: hidden; */
    overflow: hidden !important;
  }

  html {
    background-color: ${theme.colors.primaryColor};
    -webkit-touch-callout: none; /* iOS Safari */
    -webkit-user-select: none; /* Safari */
    -khtml-user-select: none; /* Konqueror HTML */
    -moz-user-select: none; /* Old versions of Firefox */
    -ms-user-select: none; /* Internet Explorer/Edge */
    user-select: none; /* Non-prefixed version, currently supported by Chrome, Edge, Opera and Firefox */
    height: 100%;
  }

  html body {
    background-color: ${theme.colors.primaryColor};
    color: ${theme.colors.secondaryColor};
    font-family: persian, roboto;
    padding: 0;
    direction: ltr;
    max-width: 500px;
    margin: 0 auto;
    font-size: 1rem;
    font-weight: normal;
    height: 100%;
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  dl {
    margin: 0;
  }

  dd {
    margin: 0;
  }

  a {
    text-decoration: none;
    color: inherit;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  p {
    margin: 0;
  }

  button {
    padding: 0;
    background-color: transparent;
    border: none;
    color: inherit;
  }

  /* Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type='number'] {
    -moz-appearance: textfield;
  }

  ::-webkit-scrollbar {
    width: 0px;
    height: 0px;
  }

  ::-webkit-scrollbar-track {
    background-color: #f7f9fc;
  }

  ::-webkit-scrollbar-thumb {
    background-color: #888;
    border-radius: 12px;
  }

  #root {
    height: 100%;
  }

  /* add global style for DatePicker */
  .DatePicker {
    width: 100%;
    .DatePicker__calendarArrow {
      border-color: transparent transparent
        ${lighten(0.1, theme.colors.primaryColor)} transparent;
    }
  }

  .date-picker-calendar {
    z-index: 100000;
    background-color: ${lighten(0.1, theme.colors.primaryColor)};
    font-size: 0.6rem !important;

    *,
    .Calendar__header .Calendar__monthArrow {
      font-size: 0.6rem;
      color: ${theme.colors.secondaryColor};
    }

    ul,
    .Calendar__weekDays,
    .Calendar__header {
      * {
        font-size: 0.9rem;
        color: ${theme.colors.secondaryColor};
      }
    }

    .Calendar__day.-disabled {
      opacity: 0.5;
    }

    .Calendar__day.-today:not(.-selectedStart):not(.-selectedEnd):not(.-selectedBetween):after,
    .Calendar__day.-today:not(.-selectedStart):not(.-selectedEnd):not(.-selectedBetween) {
      color: ${theme.colors.secondaryColor};
    }

    .Calendar__day.-today:not(.-selectedStart):not(.-selectedEnd):not(.-selectedBetween):after,
    .Calendar__monthSelectorItem:not(.-active)
      .Calendar__monthSelectorItemText:not(:disabled):hover,
    .Calendar__yearSelectorItem:not(.-active)
      .Calendar__yearSelectorText:not(:disabled):hover,
    .Calendar__monthSelectorItem:not(.-active)
      .Calendar__monthSelectorItemText:not(:disabled):hover,
    .Calendar__yearSelectorItem:not(.-active)
      .Calendar__yearSelectorText:not(:disabled):hover,
    .Calendar__day:not(.-blank):not(.-selectedStart):not(.-selectedEnd):not(.-selectedBetween):not(.-selected):hover {
      background: ${theme.colors.primaryColor};
      color: ${theme.colors.secondaryColor};
    }

    .Calendar__monthSelector,
    .Calendar__yearSelector {
      background-color: ${lighten(0.1, theme.colors.primaryColor)};
      border-radius: ${theme.radii.lg};
    }

    .Calendar__yearSelectorWrapper:before {
      background-image: linear-gradient(
        0deg,
        ${lighten(0.1, theme.colors.primaryColor)},
        ${lighten(0.1, theme.colors.primaryColor)} 10%,
        transparent
      );
    }

    .Calendar__yearSelectorWrapper:after {
      background-image: linear-gradient(
        180deg,
        ${lighten(0.1, theme.colors.primaryColor)},
        ${lighten(0.1, theme.colors.primaryColor)} 10%,
        transparent
      );
    }

    .Calendar__yearText,
    .Calendar__monthText {
      width: 7em;
      height: 2em;
      font-size: 0.9rem !important;
      padding-left: 0 !important;
      padding-right: 12px !important;
      background-color: ${theme.colors.primaryColor} !important;
      color: ${theme.colors.secondaryColor};
    }

    .Calendar__yearText::after,
    .Calendar__monthText::after {
      width: 0.4em;
      height: 0.4em;
      top: 0.8em;
      right: 6px;
      left: auto;
      border-color: ${theme.colors.secondaryColor};
    }
  }

  /* add global style for map */
  .pointer-events-none {
    pointer-events: none !important;
  }

  .vehicle-icon {
    &.tertiary {
      color: ${darken(0.05, theme.colors.tertiaryColor)};
      ::before {
        background-color: ${theme.colors.tertiaryColor};
      }
    }

    &.green {
      color: ${darken(0.05, theme.colors.greenColor)};
      ::before {
        background-color: ${theme.colors.greenColor};
      }
    }

    &.red {
      color: ${theme.colors.redColorDark};
      ::before {
        background-color: ${theme.colors.redColorDark};
      }
    }

    &.grey {
      color: ${lighten(
        theme.mode === 'dark' ? 0.6 : 0.3,
        theme.colors.blackColor,
      )};
    }
  }

  .vehicle-icon-animated {
    transition: transform 2s cubic-bezier(0, 0, 0.25, 1);
  }

  .vehicle-icon-wrap {
    padding: 4px;

    &.green .vehicle-icon-inner:before {
      background-color: ${theme.colors.greenColor};
    }

    &.red .vehicle-icon-inner:before {
      background-color: ${rgba(theme.colors.redColorDark, 0.65)};
    }

    .vehicle-icon-inner {
      padding: 12px;
      display: inline-block;
      position: relative;
      margin: 0;

      img {
        display: block;
        margin: 0;
        z-index: 2;
        position: relative;
      }

      :before {
        content: '';
        display: block;
        position: absolute;
        top: 0;
        bottom: 0;
        right: 0;
        left: 0;
        background: ${rgba(
          lighten(theme.mode === 'dark' ? 0.6 : 0.3, theme.colors.blackColor),
          0.5,
        )};
        border-radius: ${theme.radii.high};
        z-index: 1;
      }

      :after {
        content: '';
        display: block;
        position: absolute;
        top: 10px;
        bottom: 10px;
        left: 16px;
        right: 16px;
        background: ${theme.colors.primaryColor};
        z-index: 1;
        border-radius: ${theme.radii.high};
      }
    }

    :before {
      content: '';
      display: block;
      position: absolute;
      top: -2px;
      bottom: 0;
      left: -2px;
      right: 0;
      animation-delay: 0.2s;
      border-radius: ${theme.radii.high};
      border: ${theme.borders[0]};
      z-index: 1;
    }

    &.vehicle-icon-animated {
      :before,
      .vehicle-icon-inner:before {
        animation: marker-animation 2.6s ease infinite;
      }
    }
  }

  .live-location {
    width: 12px;
    height: 12px;
    background: ${theme.colors.blueColor};
    box-shadow: 0 0 8px ${theme.colors.blueColor};
    border: 2px solid #fff;
    border-radius: ${theme.radii.high};
    position: relative;
    z-index: 2;

    ::before {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      background: ${theme.colors.blueColor};
      animation: live-location-animation 2s ease infinite;
      border-radius: ${theme.radii.high};
      z-index: 1;
    }
  }

  @keyframes live-location-animation {
    from {
      transform: scale(0);
      opacity: 1;
    }
    to {
      transform: scale(6);
      opacity: 0;
    }
  }

  @keyframes marker-animation {
    from {
      transform: scale(0);
      opacity: 1;
    }
    to {
      transform: scale(1.5);
      opacity: 0;
    }
  }

  .Toastify {
    a {
      color: ${theme.colors.tertiaryColor};
    }
  }
`;

export default globalStyle;
